import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  Injector,
  input,
  linkedSignal,
  output,
  signal,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  block,
  scan,
  Scan,
  ScannerModule,
  ScannerStore,
  showRequestErrors,
  SOFTLINE_FEATURE_SCANNER,
} from '@softline/application';
import { Store } from '@softline/core';
import { FieldOkModalService } from '@softline/dynamic';
import { TranslatePipe } from '@softline/ui-core';
import { StorageLocationSearchRepository } from './repositories/storage-location-search.repository';

export interface InventoryStorageLocation {
  id: number;
  barcode: string;
  lagerplatz: string;
}

@Component({
  selector: 'soft-storage-location-search',
  imports: [CommonModule, ScannerModule, TranslatePipe],
  templateUrl: './storage-location-search.component.html',
  styleUrls: ['./storage-location-search.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class StorageLocationSearchComponent {
  private store = inject(Store);
  private injector = inject(Injector);

  private repository = inject(StorageLocationSearchRepository);
  private fieldOkService = inject(FieldOkModalService);

  readonly loading = signal(false);

  readonly scan = output<Scan>();
  readonly storageLocationChange = output<InventoryStorageLocation>();

  readonly changeable = input(true);
  readonly idlagerstamm = input<number | undefined>(undefined);

  readonly lagerplatzInput = input<InventoryStorageLocation | null | undefined>(
    undefined,
    {
      alias: 'storageLocation',
    }
  );

  readonly storageLocation = linkedSignal(this.lagerplatzInput);

  readonly canScan = computed(() => {
    return this.changeable();
  });

  readonly canSelect = computed(() => {
    return this.changeable();
  });

  constructor() {
    scan(async (scan) => {
      if (!scan || this.loading()) return;

      this.scan.emit(scan);
      this.loading.set(true);

      try {
        await block(async () => {
          try {
            const lagerplatz = await this.repository.findStockyard(scan);

            this.loading.set(false);

            if (lagerplatz) {
              this.storageLocation.set(lagerplatz as InventoryStorageLocation);
              this.storageLocationChange.emit(lagerplatz as InventoryStorageLocation);
            }
          } catch (e) {
            this.loading.set(false);
            showRequestErrors(this.store, e);
          }
        }, this.injector);
      } catch (e) {
        this.loading.set(false);
      }
    });
  }

  async onScan(): Promise<void> {
    if (!this.canScan()) return;
    await this.store.dispatch(SOFTLINE_FEATURE_SCANNER, ScannerStore.actions.scan);
  }

  async onOpenSelect(): Promise<void> {
    if (!this.canSelect()) return;

    const result: InventoryStorageLocation | 'DISMISSED' | 'CANCELED' | null =
      await this.fieldOkService.open<InventoryStorageLocation>(
        'lagerplatz',
        this.idlagerstamm()
          ? {
              idlagerstamm: this.idlagerstamm(),
            }
          : undefined,
        10_000
      );

    if (!result || result === 'DISMISSED' || result === 'CANCELED') return;

    this.storageLocation.set(result);
    this.storageLocationChange.emit(result);
  }
}
